import { Injectable, Injector } from '@angular/core';
import { OnlineStatusService } from 'ngx-online-status';
import { environment } from '../../environments/environment';
import { BehaviorSubject, catchError, map, of, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { CHANNEL_TYPE, NAME_FORMAT, PERMISSIONS } from '../enum/constants';
import { hex2rgb, setNameFormat } from '../helpers';
import { IUser } from '../interfaces';
import { LanguageService } from '../core/i18n';

interface Company {
  id: number;
  subdomain: string;
  tariff: string;
  name: string;
  next_payment_at: string;
  is_paused: boolean;
  settings: {
    id: string;
    ai_token: number;
    is_telegram_user: boolean;
    is_telegram_bot: boolean;
    is_instagram: boolean;
    is_facebook: boolean;
    is_widget: boolean;
    is_viber: boolean;
    is_whatsapp: boolean;
    is_prom: boolean
    is_ria: boolean;
    is_ringostat: boolean;
    is_jespace: boolean;
    is_tiktok: boolean;
    is_tiktok_v2: boolean;
    is_crm_v2: boolean;
    /** Shows the "take this chat / transfer to another manager" block instead of the composer in unassigned chats */
    is_chat_manager_assignment?: boolean;
    name_format?: NAME_FORMAT;
    primary_color: string;
  };
  x_data: {
    channel_count?: Record<CHANNEL_TYPE, number>;
  };
}

@Injectable({
  providedIn: 'root',
})
export class BaseService {

  public company!: Company;
  public user!: IUser;
  public userPermissions!: [];

  public currentUserInfo: any;
  public currentCompanyInfo: any;
  public currentCompanyInfoSubject$ = new BehaviorSubject(null);
  public currentCompanyInfo$ = this.currentCompanyInfoSubject$.asObservable();
  public isExpired: boolean = false;
  private localKey = 'userobj';
  private companyKey = 'companyobj';
  public network = this.statusService.getStatus();
  public isMobile!: boolean;

  constructor(
    private readonly router: Router,
    private readonly http: HttpClient,
    private statusService: OnlineStatusService,
    private readonly injector: Injector,
  ) {
    this.isMobile = window.innerWidth <= 700;
    this.statusService.status.subscribe((status) => {
      this.network = status;
    });
  }

  getProfile() {
    return this.http.get(environment.baseURL + '/profile/')
      .pipe(
        map((res: any) => res.data),
        tap((data: any) => {
          this.currentUserInfo = { ...data, permissions: this.currentUserInfo?.permissions };
          this.user = data;
          this.userPermissions = data.permissions;
          localStorage.setItem(this.localKey, JSON.stringify(data));
        }),
        switchMap((data: any) => this.applyLanguage(data?.settings?.language).pipe(
          map(() => data),
        )),
        tap((data) => this.checkPhone(data)),
      );
  }

  getCompanyInfo() {
    return this.http.get(environment.baseURL + '/companies/info')
      .pipe(
        map((res: any) => res.data),
        tap((data: any) => {
          this.currentCompanyInfo = data;
          this.company = data;
          setNameFormat(data?.settings?.name_format);
          this.currentCompanyInfoSubject$.next(data);
          if (data.settings.primary_color) {
            const rgb = hex2rgb(data.settings.primary_color);
            document.documentElement.style.setProperty('--primary-color', data.settings.primary_color);
            document.documentElement.style.setProperty('--primary-color-rgb', `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
            document.documentElement.style.setProperty('--primary-color-rgb-code', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
          }
          localStorage.setItem(this.companyKey, JSON.stringify(data));
        }));
  }

  getCompanyObj() {
    const data = localStorage.getItem(this.companyKey);
    return data ? JSON.parse(data) : {};
  }

  getCompany() {
    if (environment.default_schema) return environment.default_schema;
    const domain = window.location.hostname.split('.').reverse();
    if (domain.length !== environment.levelSubdomain + 1) return;
    if (domain[environment.levelSubdomain] === 'auth') return;
    return domain[environment.levelSubdomain];
  }

  isCompany(): boolean {
    if (environment.default_schema) return true;

    const arrayDomain = window.location.hostname.split('.').reverse();
    if ((arrayDomain.length !== environment.levelSubdomain + 1)) return false;
    const company = arrayDomain[environment.levelSubdomain];
    return !['jecrm', 'org'].includes(company);
  }


  checkPhone(user: any) {
    if (!user.phone) {
      const currentUrl = window.location.origin;
      const company = this.getCompany();
      if (!window.location.href.includes('/additional')) {
        window.location.href = currentUrl.replace(`${company}.`, 'auth.') + '/additional';
      }
    }
  }

  isHasPermission(key: PERMISSIONS): boolean {
    return this.currentUserInfo.permissions.includes(key);
  }

  checkPermission(key: PERMISSIONS): void {
    if (!this.currentUserInfo.permissions.includes(key)) this.router.navigate(['/crm/support']);
  }

  getIsExpired() {
    return this.http.get(environment.baseURL + '/olx/channels/is-expired')
      .pipe(
        map((res: any) => res.data),
        tap((is_expired: boolean) => {
          this.isExpired = is_expired;
        }));
  }

  // isMobile() {
  //   return !!navigator?.userAgent?.includes('Mobile');
  // }


  routerSettings(url: any) {
    const setPrefix = '/crm';
    return setPrefix + url;

  }

  getFile(url: string) {
    return environment.baseURL + url;
  }

  toHome() {
    // this.router.navigate(['/crm'])
    window.location.href = window.location.origin + '/crm';
  }

  toPreview() {
    window.location.href = `${window.location.protocol}//${environment.web_url}/`;
  }


  getPermissions() {
    return this.http.get(environment.baseURL + '/groups/user/permissions')
      .pipe(
        map((res: any) => res.data),
        tap((data: any) => {
          this.currentUserInfo.permissions = data;
        }));
  }

  getFileByUrl(url: string) {
    return this.http.post(environment.baseURL + '/download-by-url', { url }, { responseType: 'blob' });
  }

  setQuery(key: string, value: string) {
    this.router.navigate([], {
      queryParams: {
        [key]: value,
      },
    });
  }

  generateInvoice(param: { price: number; tariff: any; promo_code: any }) {
    return this.http.post(environment.baseURL + '/payment/generate-invoice', param, { responseType: 'blob' });
  }

  openSite(url: string) {
    window.open(url, '_blank');
  }

  private onLoadAvatar() {

  }

  /**
   * Застосовує мову з профілю користувача.
   * Ніяких перезавантажень: LanguageService підмінює словник «на льоту»,
   * а TranslationManagerService дотягує вже задіяні модулі перекладу.
   */
  private applyLanguage(language?: string) {
    const languageService = this.injector.get(LanguageService);

    return languageService.setLanguage(language ?? languageService.current).pipe(
      catchError(() => of(null)),
    );
  }
}
